import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * LAZY require (module-scope, NOT top-level `import`) — `AcImports.js` pulls
 * in `AcComps.js`, which itself top-level-imports `_translations/i18n.js`
 * (`i18n.use(...).init(...)` — throws if re-run against an already-`use`d
 * instance) and `lottie-react`. `index.ts`'s barrel evaluates EVERY module
 * it re-exports top-to-bottom on import regardless of which named export a
 * consumer actually uses (same reasoning as `display-option.service.ts`'s
 * own `getLegacyBridge()`, which this mirrors) — a top-level require here
 * would force every consumer of the barrel (unrelated canvas/panel tests
 * included) to eagerly load that chain too. Called from INSIDE the
 * component body below, only once actually rendered; Node/webpack caches
 * the module after the first call, so repeat renders are a cheap lookup.
 */
function getAcDropzone(): React.ComponentType<{
  acceptTypes: string
  handleDropImage: (files: File[]) => void
  updatedImage?: string
  showPreviews?: boolean
  resolvePreviewUrl?: () => Promise<string | null>
}> {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  return require('../../../components/UiComponents/AcImports').AcDropzone
}

/**
 * feat-004 story 2.6 (FR-11/FR-24/FR-27) — per-option dedicated image.
 *
 * Reinstated 2026-10-12 after the 2026-09-10 "image upload is out of scope"
 * removal, and deliberately rebuilt to match the Product Information tab's
 * own image field rather than the original commit-on-drop control (user
 * decision, this session): dropping a file only STAGES it — the upload and
 * the catalog commit run when the option's Save button is pressed, the way
 * `addnew.js` defers its own product-image PUT to the product save. Which is
 * why this component is now purely presentational: the two-step
 * presigned-PUT-then-commit flow lives in `display-option.service.ts`'s
 * `uploadAndCommitOptionImage`, driven by `OptionDetail`'s `handleSave`.
 *
 * `AcDropzone` (`src/components/UiComponents/AcComps.js:628-704`) is the same
 * primitive the base-image field uses — drag/drop AND click-to-browse, one
 * file, its own 5MB rejection toast. `showPreviews` hands us its thumbnail +
 * click-to-enlarge modal (globally styled, `App.css`'s `.dropzone-container
 * aside .thumb`), so a staged file and an already-committed image both render
 * exactly as they do on the Product Information tab.
 */
export interface OptionImageUploadProps {
  /** The option's COMMITTED dedicated image, resolved server-side. */
  imageUrl?: string | null
  /** From the DTO (never derived by comparing image URLs — FR-27). */
  hasDedicatedImage: boolean
  /** Object URL of a file picked but not yet saved; takes visual precedence.
   * Null when the environment has no blob URLs — a file can be staged with
   * no preview, so this is never what "is something staged?" is read from. */
  pendingPreviewUrl?: string | null
  /** Whether a file is staged, independent of whether it can be previewed. */
  hasPendingImage?: boolean
  /** AC5: gates the control itself (`displayOption.image.upload`). */
  canUpload: boolean
  /** True while a save is in flight — the dropzone stops accepting drops. */
  disabled?: boolean
  /** Hands the staged file up to `OptionDetail`, which saves it on Save. */
  onFileSelected: (file: File) => void
  /** fix-017: resolves the committed dedicated image's full-size url, so the
   * click-to-enlarge preview shows it big the way the product image's does. */
  resolveFullImageUrl?: () => Promise<string | null>
}

export const OptionImageUpload: React.FC<OptionImageUploadProps> = ({
  imageUrl,
  hasDedicatedImage,
  pendingPreviewUrl,
  hasPendingImage = false,
  canUpload,
  disabled = false,
  onFileSelected,
  resolveFullImageUrl,
}) => {
  const { t } = useTranslation()
  // Only required (see getAcDropzone's comment) when actually needed — a
  // read-only render (`canUpload: false`) never pulls the legacy chain in.
  const AcDropzone = canUpload ? getAcDropzone() : null

  // A staged file wins over the committed one (that IS the pending change);
  // otherwise show the committed dedicated image, and nothing at all when the
  // option has none — the base product photo is deliberately NOT used as a
  // stand-in here (reported 2026-09-10: a generic photo made "has its own
  // image" and "falls back" visually indistinguishable).
  const previewUrl =
    pendingPreviewUrl || (hasDedicatedImage && imageUrl ? imageUrl : '')

  const handleDropImage = (files: File[]) => {
    if (disabled) return
    const file = files && files[0]
    if (!file) return
    onFileSelected(file)
  }

  return (
    <div className="simv3-doc__field" data-testid="option-image-upload">
      <label className="simv3-doc__field-label">
        {t('img', { defaultValue: 'Image' })}
      </label>
      {canUpload && AcDropzone ? (
        <div
          className="simv3-doc__image-upload-dropzone"
          style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          <AcDropzone
            updatedImage={previewUrl}
            acceptTypes="image/png, image/jpeg, image/jpg, image/webp, image/avif"
            handleDropImage={handleDropImage}
            showPreviews
            // Only the COMMITTED dedicated image has a stored full-size copy;
            // a staged file is previewed from the file itself.
            resolvePreviewUrl={
              hasDedicatedImage && !pendingPreviewUrl ? resolveFullImageUrl : undefined
            }
          />
          {hasPendingImage || pendingPreviewUrl ? (
            <span
              className="simv3-doc__lock-reason"
              data-testid="option-image-pending"
            >
              {t('simulationV3.displayOptions.image.pending', {
                defaultValue: 'Will be uploaded when you save',
              })}
            </span>
          ) : null}
        </div>
      ) : (
        <>
          {/* Not allowed to CHANGE the picture is not a reason to be unable to
              SEE it — the same thumbnail markup `AcDropzone` renders under
              `showPreviews`, so read-only and editable look alike (styled
              globally by `App.css`'s `.dropzone-container aside .thumb`). */}
          {previewUrl ? (
            <div className="dropzone-container">
              <aside>
                <div className="thumb">
                  <img src={previewUrl} alt="" data-testid="option-image-readonly-thumb" />
                </div>
              </aside>
            </div>
          ) : null}
          <span
            className="simv3-doc__readonly-value"
            data-testid="option-image-upload-readonly"
          >
            {t('simulationV3.displayOptions.image.readOnly', {
              defaultValue: 'Image upload is not available',
            })}
          </span>
        </>
      )}
    </div>
  )
}
